import React from 'react';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Preamble } from 'components/helpers/Molecules/Preamble/Preamble';
import clsx from 'clsx';
import { ContentOverride } from '../CardListingShared';
import { normalizeStandardImageUrl } from '../CardListingShared';
import { SearchRecord } from 'lib/services/models/search-response';
import { Themes } from 'lib/themes';
import styles from './CategoryTiles.module.scss';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { sendContentResultAnalytics } from 'components/helpers/Analytics/CategoryTilesAnalytics';
import { CtaFields, HeadlineFields } from 'lib/types/headlineFields';

export interface CategoryTilesProps extends HeadlineFields, CtaFields {
  categories: SearchRecord[];
  theme?: Themes;
  showSliderForMobile?: boolean;
  contentOverrides?: Record<string, ContentOverride>;
  fallbackImageUrl?: string;
}

const Tile = ({
  category,
  headline,
  fallbackImageUrl,
  showSliderForMobile,
  removeTitleLink,
}: {
  category: SearchRecord;
  headline?: React.ReactNode;
  fallbackImageUrl?: string;
  showSliderForMobile?: boolean;
  removeTitleLink?: boolean;
}) => {
  const language = useGlobalData().locale;
  const imageSrc =
    normalizeStandardImageUrl(category.StandardImageUrl, category.PageUrl) ||
    normalizeStandardImageUrl(fallbackImageUrl, category.PageUrl);

  const navigationUrl =
    category.ContentType === 'Recipe' &&
    !(category.ItemUrl.includes('https://') || category.ItemUrl.includes('http://'))
      ? `/recipes/${category.ItemUrl}`
      : category.ItemUrl;

  return (
    <CtaLink
      className={clsx(styles.categoryTile, {
        [styles.categoryTileSlider]: showSliderForMobile,
        [styles.categoryTileStatic]: removeTitleLink,
      })}
      url={navigationUrl}
      aria-label={category.Title}
      ctaStyle="none"
      target="_self"
      onClick={() => {
        sendContentResultAnalytics({
          content: {
            ContentId: category.ContentId ?? '',
            Title: category.Title ?? '',
          } as Partial<BaseContentResponse>,
          language,
          contentType: category.ContentType || 'Category',
          recordType: category.RecordType || '',
          contentListName:
            (React.isValidElement(headline) &&
              (headline.props as { field?: { value?: string } }).field?.value) ||
            '',
          templateName: 'categoryTile',
        });
      }}
    >
      {imageSrc ? (
        <EnhancedNextImage
          src={imageSrc}
          alt={category.Title}
          className={styles.categoryTileImage}
          height={120}
          width={120}
        />
      ) : null}
      <div className={styles.categoryTileTitle}>{category.Title}</div>
    </CtaLink>
  );
};

export const CategoryTiles = (props: CategoryTilesProps) => {
  const categories = props.categories.slice(0, 14); // Limit to first 14 categories
  const contentOverrides: Record<string, ContentOverride> = props.contentOverrides || {};
  const fallbackImageUrl = props.fallbackImageUrl;
  if (contentOverrides && Object.keys(contentOverrides).length > 0) {
    categories.forEach((category: SearchRecord) => {
      const override = contentOverrides[category.ContentId];
      if (override) {
        category.StandardImageUrl =
          normalizeStandardImageUrl(override.image, category.PageUrl) || category.StandardImageUrl;
        category.Title = override.title || category.Title;
        category.ItemUrl = override.url || category.ItemUrl;
      }
    });
  }

  return (
    <BrandAndThemeProvider
      theme={props.theme}
      className={clsx(styles)}
      data-component="authorable/shared/card-listing/category-tiles/category-tiles"
    >
      <div className={styles.categoryTilesWrapper}>
        <Preamble
          headline={props.headline}
          headlineDescription={props.headlineDescription}
          headlineEyebrow={props.headlineEyebrow}
          cta1={props.cta1}
          cta2={props.cta2}
          align="TopCenter"
        >
          <div
            className={clsx({
              [styles.mobileSliderContainer]: props.showSliderForMobile,
            })}
          >
            <ul
              className={clsx(styles.categoryTilesContent, {
                [styles.mobileSliderList]: props.showSliderForMobile,
              })}
            >
              {categories.map((category: SearchRecord) => (
                <li key={category.ContentId} className={styles.categoryTilesList}>
                  <Tile
                    category={category}
                    headline={props.headline}
                    fallbackImageUrl={fallbackImageUrl}
                    showSliderForMobile={props.showSliderForMobile}
                    removeTitleLink={contentOverrides[category.ContentId]?.removeTitleLink}
                  />
                </li>
              ))}
            </ul>
          </div>
        </Preamble>
      </div>
    </BrandAndThemeProvider>
  );
};
